// Изборът на период в склада — ЕДИНСТВЕНИЯТ в модула (справките, складовата
// карта): бутон „пресет + дати", а при клик — попъп със седемте пресета и
// календар (`PeriodPickerPanel`). Изборът се прилага ВЕДНАГА и попъпът се
// затваря: пресетът — с едно натискане, календарът — с втория клик (краят на
// диапазона). „Отказ/Приложи" няма: Esc или клик извън попъпа затварят БЕЗ
// промяна. Попъпът е antd Popover, затова общият пазач за отворен слой
// (hasOpenOverlay) го вижда. Изходът е [начало на първия ден, начало на
// последния] — `periodToParams` го разтяга до края на деня.
//
// Клавиатура: Enter/Space/↓ върху бутона отварят и фокусът отива на текущия
// пресет; Esc (с preventDefault) затваря и връща фокуса на бутона.
//
// src/components/ReportDatePicker НЕ се пипа — той е общ със Справки, Одит и
// ФМ отчета; този живее само в склада.
import { useRef, useState, type KeyboardEvent } from "react";
import { Popover } from "antd";
import { CalendarOutlined, DownOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { PeriodPickerPanel } from "./PeriodPickerPanel";
import { formatPeriod, presetOfPeriod, type Period } from "./periodPresets";

type Props = {
  value: Period;
  onChange: (period: Period) => void;
};

const sameDays = (a: Period, b: Period) => a.from.isSame(b.from, "day") && a.to.isSame(b.to, "day");

export const PeriodPicker = ({ value, onChange }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // `returnFocus` — затварянето е от клавиатурата или от избор: фокусът се
  // връща на бутона, за да продължи Tab-ът оттам
  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };
  const apply = (period: Period) => {
    // Същият период не пуска нова заявка и не нулира „Покажи още"
    if (!sameDays(period, value)) onChange(period);
    close(true);
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" && !open) {
      event.preventDefault();
      setOpen(true);
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      close(true);
    }
  };

  const preset = presetOfPeriod(value);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      arrow={false}
      content={<PeriodPickerPanel value={value} onApply={apply} onClose={close} />}
      classNames={{ body: "!p-0" }}
      destroyOnHidden
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onKeyDown={onTriggerKeyDown}
        className="flex min-w-0 cursor-pointer items-center gap-2.5 whitespace-nowrap rounded-lg border border-shade-3 bg-surface px-3 py-2 text-ink outline-none hover:border-shade-5 focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)]"
      >
        <CalendarOutlined className="text-faint" />
        <span className="flex min-w-0 flex-col items-start leading-[1.2]">
          <span className="text-[11px] text-muted">
            {preset ? t(`inventoryPage.period_${preset}`) : t("inventoryPage.periodCustom")}
          </span>
          <span className="truncate font-sheet text-[13px] font-semibold">{formatPeriod(value)}</span>
        </span>
        <DownOutlined className="text-[10px] text-faint" />
      </button>
    </Popover>
  );
};

export default PeriodPicker;
